import { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../api.js';
import { Card, SectionHeader, Button, CollapsibleSection, SegmentedControl } from '../components/ui';
import EmptyState from '../components/EmptyState.jsx';
import AnalyticsScope from '../components/AnalyticsScope.jsx';
import { toCsv, downloadCsv, csvFilename } from '../util/csv.js';
import { convertingRows, CONVERTING_COLUMNS } from '../util/convertingCsv.js';
import CohortGrid from '../components/charts/CohortGrid.jsx';
import BoxRange from '../components/charts/BoxRange.jsx';
import TodayCard from '../components/TodayCard.jsx';
import AnalyticsRetroCard from '../components/AnalyticsRetroCard.jsx';
import AnalyticsClustersCard from '../components/AnalyticsClustersCard.jsx';
import BarChart from '../components/charts/BarChart.jsx';
import LineChart from '../components/charts/LineChart.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import FunnelChart, { SEGMENTS } from '../components/charts/FunnelChart.jsx';
import HeatmapChart from '../components/charts/HeatmapChart.jsx';
import { SERIES, LABEL, LABEL_STRONG, softFill, soft } from '../components/charts/chartColors.js';
import MeterBar from '../components/charts/MeterBar.jsx';
import { ChartTooltip, useChartHover } from '../components/charts/ChartTooltip.jsx';
import { useUrlFilters } from '../hooks/useUrlFilters.js';
import { useNavigate } from 'react-router-dom';

// Analytics view state, mirrored to the URL so a filtered view is shareable +
// Back-able (same pattern as ApplicationsPage). Phase 1 of the viz roadmap —
// the range toggle moves here; dimension filters join it in a later slice.
const ANALYTICS_FILTER_SPEC = {
  range: { param: 'range', kind: 'enum', default: 'all' },
  // T328 — the five dimensions plus a window. `set` so each is a chip group
  // that ORs within itself; the URL carries them comma-joined, so a filtered
  // view is shareable and back-able like every other filter in the app.
  source: { param: 'src', kind: 'set', default: '' },
  country: { param: 'cty', kind: 'set', default: '' },
  company: { param: 'co', kind: 'set', default: '' },
  band: { param: 'band', kind: 'set', default: '' },
  outcome: { param: 'out', kind: 'set', default: '' },
  from: { param: 'from', kind: 'text', default: '' },
  to: { param: 'to', kind: 'text', default: '' },
  // T203 — how the "What's converting" tables are ordered. The backend sorts
  // them applied-desc, which answers "where did I send the most" — a question
  // about the operator's habits. The question the section is titled after is
  // "where does it work", and that is the rate. Mirrored to the URL so a
  // sorted view is shareable like every other filter in the app.
  rateSort: { param: 'rsort', kind: 'enum', default: 'volume' },
};

const RATE_SORT_OPTIONS = [
  { value: 'volume', label: 'Most sent' },
  { value: 'rate', label: 'Best rate' },
];

// Rows below the small-N floor sink to the bottom whichever way it is sorted:
// a 1-for-1 source is 100% and would otherwise top the list on rate, which is
// exactly the false lead the floor exists to suppress.
export function sortRateRows(rows, mode, minN) {
  const out = [...(rows ?? [])];
  if (mode !== 'rate') return out;
  return out.sort((a, b) => {
    const aThin = (a.applied ?? 0) < minN;
    const bThin = (b.applied ?? 0) < minN;
    if (aThin !== bThin) return aThin ? 1 : -1;
    return (b.advanceRatePct ?? -1) - (a.advanceRatePct ?? -1) || (b.applied ?? 0) - (a.applied ?? 0);
  });
}
// Date-range presets. Windows the appliedAt-driven cards; the lifecycle/
// calibration cards stay all-time (and say so when a range is active).
const RANGE_OPTIONS = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: 'all', label: 'All time' },
];

// T033/T059 + Analytics-Roadmap Phase 1 — read-only application analytics over
// GET /analytics/funnel + the existing A/B stats. Theme-safe ui/ primitives;
// stage colours mirror the ApplicationsPage Kanban. Small-N honesty: any rate
// cell with too few samples shows raw counts / a muted dash, never a noisy %.

const MIN_N = 3; // below this denominator, rates are noise → show raw / muted
// T260 — the per-arm floor for the reply-rate slices. Higher than MIN_N because
// these compare two arms of ~15 rather than rank many small ones; mirrors
// RATE_MIN_N in analyticsLeanIn.js, which decides whether the same data earns a
// written prescription.
const RATE_MIN_N = 5;
// T264(1) — the score card used to mix two thresholds: mutedOf and the legend
// said 5, valueText and rateCell said MIN_N (3). A band with n=3 or 4 was
// therefore greyed as "too few to read" AND printed with a percentage beside
// it, contradicting itself in the same row. 5 is the number the backend uses
// to decide whether the same bands earn a written claim (SCORE_MIN_ACTED in
// analyticsLeanIn.js), so it is the one that survives.
const SCORE_MIN_N = 5;

// Dismissal reasons are categories with no order, so they get distinguishable
// tints of the theme's own tokens rather than a rainbow: the palette has to
// survive all seven themes, and soft() is the only theme-safe way to vary one.
// "Tagged" has to mean tagged. `dismissReasons.total` is every dismissed row,
// including the ones this very card excludes: bulk preference-cleanup sweeps and
// rows with no reason at all. On the operator's corpus that is 325 against 167
// actually tagged — and while the section is folded the summary is the ONLY
// number he sees, so it contradicted the donut two lines below it.
export function dismissSummary(dr) {
  if (!dr) return null;
  const tagged = (dr.presets ?? []).reduce((n, p) => n + (p.count ?? 0), 0) + (dr.other ?? 0);
  const skipped = (dr.total ?? 0) - tagged;
  return skipped > 0 ? `${tagged} tagged · ${skipped} untagged or bulk` : `${tagged} tagged dismissals`;
}

const DISMISS_COLORS = [
  SERIES.down,
  soft(SERIES.down, 65),
  SERIES.warn,
  soft(SERIES.warn, 65),
  SERIES.accent,
  soft(SERIES.accent, 65),
  LABEL,
];

const NoData = () => <span aria-label="no data">—</span>;
const pctText = (v) => (v == null ? <NoData /> : `${v}%`);
const daysText = (v) => (v == null ? <NoData /> : `${v}d`);
// A distribution stat (median/p90) is only honest with enough samples — below
// MIN_N it's a single point dressed as a percentile, so show a muted dash.
// rate cell honouring the small-N contract
const rateCell = (rateVal, den, min = MIN_N) =>
  den < min ? <span className="text-slate-600" title="too few to read">—</span> : pctText(rateVal);

// Deterministic, honest A/B significance note — never claims a win on thin data.
// T343 — exported so the table above the note cannot disagree with it. The
// note stayed silent under five picks per arm while `rateCell` printed a rate
// from three, so a four-pick arm showed "50%" beside "Too few to call". One
// number said readable and the sentence under it said not.
export const AB_MIN_N = 5;

function significanceNote(aN, aAdv, bN, bAdv, labels = ['A', 'B']) {
  const MIN = AB_MIN_N;
  if (aN < MIN || bN < MIN) return `Too few to call — aim for ~${MIN}+ each (${labels[0]} ${aN}, ${labels[1]} ${bN}).`;
  const aRate = aAdv / aN;
  const bRate = bAdv / bN;
  if (Math.abs(aRate - bRate) < 0.1) return 'No clear difference yet.';
  const leader = aRate > bRate ? labels[0] : labels[1];
  return `${leader} leads (${Math.round(aRate * 100)}% vs ${Math.round(bRate * 100)}%) — watch it as more data lands.`;
}

// Phase 4 — a compact label · bar · count list for the disposition breakdowns.
// Rows with zero count are hidden (keeps the card honest on thin data); the bar
// is proportional to the largest row so the dominant outcome reads at a glance.
function DispoRows({ rows, labelWidth = 'w-40', empty = 'Nothing recorded yet.' }) {
  const shown = rows.filter(([, count]) => count > 0);
  if (!shown.length) return <p className="text-xs text-slate-500">{empty}</p>;
  const max = Math.max(...shown.map(([, count]) => count));
  return (
    <div className="space-y-1.5">
      {shown.map(([label, count, accent]) => (
        <div key={label} className="flex items-center gap-2 text-xs">
          <span className={`${labelWidth} shrink-0 truncate text-slate-400`} title={label}>{label}</span>
          <div className="flex-1">
            <MeterBar frac={count / max} />
          </div>
          <span className={`w-6 text-right tabular-nums ${accent || 'text-slate-300'}`}>{count}</span>
        </div>
      ))}
    </div>
  );
}

// Per-card loading skeleton — a pulsing placeholder that mirrors the page
// shape so the load reads as "filling in" rather than a blank flash. Theme-safe
// (surface var, no Tailwind opacity class) + honours prefers-reduced-motion.
function SkeletonBlock({ className = '' }) {
  return (
    <div
      className={`animate-pulse rounded motion-reduce:animate-none ${className}`}
      style={{ background: 'var(--c-surface-2)' }}
    />
  );
}

function AnalyticsSkeleton() {
  return (
    <div className="space-y-6" role="status" aria-live="polite">
      <span className="sr-only">Loading analytics…</span>
      {[0, 1, 2, 3].map((i) => (
        <Card key={i} padding="md">
          <SkeletonBlock className="h-3 w-44" />
          <SkeletonBlock className={`mt-3 w-full ${i % 2 === 0 ? 'h-24' : 'h-16'}`} />
        </Card>
      ))}
    </div>
  );
}

function Tile({ label, value, sub, accent = 'text-slate-100' }) {
  return (
    <Card padding="md">
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${accent}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-500">{sub}</p>}
    </Card>
  );
}

// Ranked advance-rate bars for a dimension (source / country / company / CV),
// ordered by volume (the backend sorts applied-desc). The bar encodes the
// advance rate; raw advanced/applied + ghosting sit alongside. Small-N honest:
// rows under MIN_N draw no bar and dash the rate. Theme-safe — track/fill come
// from CSS vars (var(--c-surface-2) / var(--c-up)), never a Tailwind opacity
// class. Hover (title) surfaces the full detail.
// `minN` and `verb` are parameters because this table is reused for two
// different measurements. The apply-lag card feeds it REPLY counts under a
// 5-per-arm floor, and it was hardcoded to say "advanced" at a floor of 3 —
// so the card greyed rows its own section hint said it would print, and its
// tooltip named a metric it was not showing.
function RateTable({ rows, keyOf, labelOf, onSelect, onSelectGhosted, minN = MIN_N, verb = 'advanced', sort = 'volume' }) {
  // T258(3) — the full detail (raw counts, ghosted, "too few to read") lived in
  // a native title=, which appears on mouse hover only: a keyboard user tabbing
  // through these rows got the truncated label and nothing else. Same
  // ChartTooltip the SVG charts use, driven by focus as well as hover.
  const { hover, show, hide } = useChartHover();
  const wrapRef = useRef(null);
  const [wrapW, setWrapW] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setWrapW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (!rows.length) return <p className="text-xs text-slate-500">No data yet.</p>;
  return (
    <div className="relative space-y-2" ref={wrapRef}>
      {sortRateRows(rows, sort, minN).map((r) => {
        const muted = r.applied < minN;
        const rate = r.advanceRatePct ?? 0;
        const baseTitle = `${labelOf(r)}: ${r.advanced}/${r.applied} ${verb}${r.ghosted ? ` · ${r.ghosted} gone quiet (no movement 14d+)` : ''}${muted ? ' · too few to read' : ` · ${rate}%`}`;
        const inner = (
          <>
            <div className="mb-0.5 flex items-baseline justify-between gap-2">
              <span className="min-w-0 truncate text-slate-300 group-hover:text-brand-400" title={labelOf(r)}>{labelOf(r)}</span>
              <span className="shrink-0 tabular-nums">
                <span className="text-slate-500">{r.advanced}/{r.applied}</span>
                {/* T352 — "gone quiet", not "ghosted (no reply)". The count now
                    includes rows that DID get a reply and then went silent,
                    which is what the list behind the link has always shown. */}
                {r.ghosted > 0 && <span className="ml-1.5 text-amber-400">{r.ghosted} quiet</span>}
                <span className="ml-1.5 text-slate-100">{muted ? <span className="text-slate-600">—</span> : `${rate}%`}</span>
              </span>
            </div>
            {/* T337 — the ghosted share, drawn from the right edge.
                The row printed "12 ghosted" beside a bar showing only the
                advance rate, so the eye ranked sources by the bar and the worst
                number on the row was the one it could not see. */}
            <MeterBar
              frac={rate / 100}
              overlayFrac={r.applied > 0 ? (r.ghosted ?? 0) / r.applied : 0}
              muted={muted}
              color={SERIES.up}
              overlayColor={SERIES.warn}
              rounded="sm"
            />
          </>
        );
        const detail = onSelect ? `${baseTitle} · click to see these applications` : baseTitle;
        // Anchor at the row's own top edge, in wrapper coords, so the tooltip
        // lands on the row the pointer or focus is actually on.
        const at = (e) => {
          const box = e.currentTarget.getBoundingClientRect();
          const wrap = wrapRef.current?.getBoundingClientRect();
          show(box.left - (wrap?.left ?? 0) + box.width / 2, box.top - (wrap?.top ?? 0), detail);
        };
        const handlers = { onMouseEnter: at, onMouseLeave: hide, onFocus: at, onBlur: hide };
        // T261 — the ghosted count named the operator's real problem and was a
        // native title. It gets its own target: the same dimension drill, plus
        // the tracker's stale filter, so the click lands on exactly the rows the
        // number is about. A separate button rather than a nested one — a
        // <button> inside a <button> is invalid and the inner one never fires.
        return (
          <div key={keyOf(r)} className="text-xs">
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(r)}
                aria-label={detail}
                className="group block w-full cursor-pointer text-left text-xs"
                {...handlers}
              >
                {inner}
              </button>
            ) : (
              <div tabIndex={0} aria-label={detail} {...handlers}>
                {inner}
              </div>
            )}
            {r.ghosted > 0 && onSelectGhosted && (
              <button
                type="button"
                onClick={() => onSelectGhosted(r)}
                className="mt-0.5 text-[10px] text-amber-400 underline decoration-dotted hover:text-amber-300"
              >
                open the {r.ghosted} that went quiet →
              </button>
            )}
          </div>
        );
      })}
      <ChartTooltip hover={hover} width={wrapW} />
    </div>
  );
}

// T308 — say WHY a row could not be measured, not just how many.
//
// This footnote used to read "N with no usable posting date" for every excluded
// row. On the operator's data that is 59 — and 56 of them are jobs he pasted
// himself, where `postedAt` holds the moment he added it and there is no posting
// date to be missing (T307). Telling him a field is unusable when the row never
// carried one sends him hunting an ingestion bug that does not exist.
function applyLagFootnote(lag) {
  const by = lag?.unknownBy;
  if (!by) {
    // Older payload shape, before the breakdown existed.
    return lag?.unknown > 0 ? ` · ${lag.unknown} not measurable` : null;
  }
  const parts = [];
  if (by.ownAdded) parts.push(`${by.ownAdded} you added yourself`);
  if (by.noDate) parts.push(`${by.noDate} with no posting date`);
  if (by.appliedBeforePosted) parts.push(`${by.appliedBeforePosted} dated after you applied`);
  return parts.length ? ` · excluded: ${parts.join(', ')}` : null;
}

export default function AnalyticsPage() {
  const [filters, setFilters] = useUrlFilters(ANALYTICS_FILTER_SPEC);
  const range = filters.range;
  const rateSort = filters.rateSort;
  const setRange = useCallback((r) => setFilters({ range: r }), [setFilters]);
  const navigate = useNavigate();
  // Drill into the application board pre-filtered to a dimension value.
  const drillTo = useCallback(
    (key, value, extra) => {
      const qs = new URLSearchParams({ [key]: value, ...(extra ?? {}) });
      navigate(`/applications?${qs}`);
    },
    [navigate],
  );
  const [data, setData] = useState(null);
  const [variants, setVariants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  // T164 — abort the previous in-flight load when a new one starts (range
  // switch, focus refetch). Without this, a slow earlier response could resolve
  // last and overwrite the correct data — switching 7d→30d→90d quickly showed
  // the wrong window. mountedRef only guarded post-unmount, not out-of-order.
  const [refreshing, setRefreshing] = useState(false);
  const loadAbortRef = useRef(null);
  useEffect(
    () => () => {
      mountedRef.current = false;
      loadAbortRef.current?.abort();
    },
    [],
  );

  const load = useCallback(
    async (silent = false) => {
      loadAbortRef.current?.abort(); // cancel any superseded request
      const ctrl = new AbortController();
      loadAbortRef.current = ctrl;
      if (!silent) setLoading(true);
      try {
        const tz = -new Date().getTimezoneOffset();
        const [funnel, ab] = await Promise.all([
          // T328 — `include=rows` is what lets the filter bar re-scope without a
          // second round trip. Opt-in on the server precisely so the pages that
          // do not filter keep the lean response.
          api.get(`/analytics/funnel?range=${range}&tzOffset=${tz}&include=rows`, { signal: ctrl.signal }),
          api.get('/recommendations/cv-variant-stats', { signal: ctrl.signal }),
        ]);
        if (!mountedRef.current || ctrl.signal.aborted) return;
        setData(funnel);
        setVariants(ab);
        setError(null);
      } catch (e) {
        // A superseded/aborted request is not an error — just stop.
        if (e?.name === 'AbortError' || ctrl.signal.aborted) return;
        if (mountedRef.current) setError(e?.body?.message || e?.message || 'Failed to load analytics');
      } finally {
        if (mountedRef.current && !silent && !ctrl.signal.aborted) setLoading(false);
      }
    },
    [range],
  );

  // T257(2) — changing 7d→30d threw all sixteen sections into skeleton, even
  // though what was on screen was still valid: the page went blank and rebuilt
  // itself for a refinement. Only the FIRST load blanks the page; every later
  // range change refetches silently behind the data already shown, with a small
  // marker next to the toggle so the wait is still visible.
  const firstLoadRef = useRef(true);
  useEffect(() => {
    const first = firstLoadRef.current;
    firstLoadRef.current = false;
    if (!first) setRefreshing(true);
    load(!first).finally(() => {
      if (mountedRef.current) setRefreshing(false);
    });
  }, [load]);

  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState !== 'hidden') load(true);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  const setGoal = useCallback(
    async (next) => {
      const clamped = Math.max(0, Math.min(50, Math.round(next)));
      setData((prev) => {
        if (!prev?.cadence) return prev;
        const g = clamped || null;
        const c = prev.cadence;
        return {
          ...prev,
          cadence: {
            ...c,
            goal: g,
            goalMetToday: g ? c.appliedToday >= g : false,
            daysHitGoalThisWeek: g ? c.byDay.filter((d) => d.count >= g).length : 0,
          },
        };
      });
      try {
        await api.patch('/preferences', { dailyApplicationGoal: clamped || null });
      } catch {
        /* reconciled by the refetch below */
      } finally {
        load(true);
      }
    },
    [load],
  );

  const rt = data?.responseTime;
  // Applications that are neither closed out nor silent — the slice that makes
  // the outcomes donut a whole rather than a sample of it.
  const openNow = data
    ? Math.max(
        0,
        (data.totals?.applied ?? 0) -
          (data.outcomes?.offer ?? 0) -
          (data.outcomes?.rejected ?? 0) -
          (data.outcomes?.withdrawn ?? 0) -
          (data.outcomes?.stalled ?? 0) -
          // T275 — applications taken off the tracker. Without this term they
          // fell into the residual and the donut drew them as still open.
          (data.outcomes?.offTracker ?? 0),
      )
    : 0;
  // The lifecycle/calibration cards are all-time by design; say so when a date
  // range is active, so an unchanged trend/heatmap doesn't read as a bug.
  const allTimeNote = range !== 'all' ? ' Shown all-time — not narrowed by the date range.' : '';

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-100">Analytics</h1>
        <div className="flex items-center gap-1.5">
          {refreshing && (
            <span className="mr-1 text-xs text-slate-500" role="status">
              <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-brand-500" />
              updating…
            </span>
          )}
          {RANGE_OPTIONS.map((o) => (
            <Button
              key={o.value}
              variant={range === o.value ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setRange(o.value)}
            >
              {o.label}
            </Button>
          ))}
        </div>
      </header>

      {/* T328 — the answer to "cannot filter". Renders nothing until rows
          arrive, and nothing at all on an account with no applications. */}
      {!loading && !error && data?.rows?.length > 0 && (
        <AnalyticsScope rows={data.rows} filters={filters} setFilters={setFilters} />
      )}

      {loading && <AnalyticsSkeleton />}
      {/* T249 — was a bare red sentence: it told the operator the page was
          broken and gave him nothing to do about it but reload the browser. */}
      {/* T290 — `() => load()`, not `load`. As a bare reference the click event is
          passed as the first argument, and `load(silent = false)` reads it as
          truthy: the retry ran with `silent` on, so `setLoading` never fired and
          the screen sat on "Could not load analytics" while a request was in
          flight behind it. The button looked dead. */}
      {error && !loading && (
        <EmptyState icon="⚠" title="Could not load analytics" body={error} actions={[{ label: 'Retry', onClick: () => load() }]} />
      )}



      {!loading && !error && data?.cadence && (
        <section className="space-y-2">
          <SectionHeader title="Today" hint="Your application pace. Updates when you mark a job Applied." />
          <TodayCard cadence={data.cadence} onSetGoal={setGoal} />
        </section>
      )}


      {!loading && !error && data && data.totals.applied === 0 && (
        <EmptyState
          icon="▤"
          title={range !== 'all' ? 'No applications in this range' : 'No applications yet'}
          body={
            range !== 'all'
              ? 'Nothing was applied to inside this window. Widen it, or mark a shortlisted job as Applied from the dashboard.'
              : 'Mark a shortlisted job as Applied from the dashboard. Once you have a few in flight, your funnel and response rates show up here.'
          }
          /* T257(3) — an empty RANGE almost always means the range is too
             narrow, not that there is nothing to see; the only offer used to be
             leaving the page. */
          actions={
            range !== 'all'
              ? [
                  { label: 'Show all time', onClick: () => setRange('all'), primary: true },
                  { label: 'Go to dashboard', to: '/' },
                ]
              : [{ label: 'Go to dashboard', to: '/', primary: true }]
          }
        />
      )}


      {!loading && !error && data && data.totals.applied > 0 && (
        <>

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Tile label="Applications" value={data.totals.applied} sub={`${data.totals.offers} offer${data.totals.offers === 1 ? '' : 's'}`} />
            <Tile label="Reply rate" value={pctText(data.rates.replyRatePct)} sub={`${data.totals.replied} of ${data.totals.applied} got a reply (incl. rejections)`} accent="text-brand-400" />
            <Tile label="Interview rate" value={pctText(data.rates.interviewRatePct)} sub={`${data.totals.interviews} reached interview`} accent="text-amber-300" />
            <Tile label="Offer rate" value={pctText(data.rates.offerRatePct)} sub={`${data.totals.offers} reached offer`} accent="text-emerald-300" />
          </div>

          {/* Funnel with drop-off split */}
          <section className="space-y-2">
            <SectionHeader title="Pipeline funnel" hint="How far applications progress, and where they die. Each bar counts every application that ever reached that stage; segments split advanced / open / died. Hover for the counts." />
            <Card padding="md">
              {/* T340 — a rung opens the applications that REACHED it, which is
                  not the same set as the ones sitting there now: an application
                  rejected after two interviews reached `interview`. */}
              <FunnelChart
                data={data.funnel}
                ariaLabel="Application pipeline funnel"
                onSelect={(stage) => drillTo('reached', stage, { show: 'all' })}
              />
              {/* T264(2) — generated from the chart's own SEGMENTS. The legend
                  used to restate all three fills by hand, including a literal
                  30% that no longer matched the chart after T258 raised it. */}
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500">
                {SEGMENTS.map((seg) => (
                  <span key={seg.key} className="inline-flex items-center gap-1">
                    <span
                      className="inline-block h-2 w-2 rounded-sm"
                      style={{ background: seg.fill, outline: seg.stroke ? `1px solid ${seg.stroke}` : undefined }}
                    />{' '}
                    {seg.label}
                  </span>
                ))}
              </div>
            </Card>
          </section>


        </>
      )}

      {/* Where to lean in — deterministic prescriptions from the operator's own
          numbers, each carrying raw counts. Silent until a signal clears MIN_N. */}
      {!loading && !error && data?.leanIn && (data.leanIn.hasData || data.totals?.applied > 0) && (
        <section className="space-y-2">
          {/* T264(4) — the hint named only the sample floor, so a silent 3-vs-3
              split read as a bug rather than as the gate doing its job. State
              the whole gate: samples AND a real gap AND real events. */}
          <SectionHeader
            title="Where to lean in"
            hint="Plain prescriptions from your own numbers — every line carries the raw counts. Each kind of line has its own bar to clear before it is printed: a source, country or CV comparison needs 3 applications per side, a 15-point gap and 2 real advances; a weekday or posting-freshness line needs 5 per side and a 15-point gap; a score-band claim needs 5 acted-on per band and a 25-point spread; a silence warning needs 3 applications from that source with at least half of them gone quiet. Most honest splits stay quiet."
          />
          {data.leanIn.hasData ? (
            <Card padding="md">
              <ul className="space-y-2">
                {data.leanIn.insights.map((ins, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className={'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ' + (ins.tone === 'warning' ? 'bg-amber-500' : 'bg-emerald-500')} />
                    <span className="text-slate-200">{ins.text}</span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : (
            <Card padding="md">
              <p className="text-xs text-slate-500">
                Not enough to call yet. Once a few of your applications start advancing (or clearly stalling) within the same source, country, CV version, or score band, plain prescriptions show up here.
              </p>
            </Card>
          )}
        </section>
      )}


      {!loading && !error && data && data.totals.applied > 0 && (
        <>

          {/* ── T259 — the first rate over time this page has ever had ─────
              Volume per week existed; conversion per week did not, so "am I
              converting better than last month?" had no answer at all. The
              honesty problem is right-censoring: the newest cohorts have not
              had time to be answered, so a naive series always falls off a
              cliff at the right edge. Those points are drawn provisionally and
              said so, rather than dropped (they are the ones he most wants to
              see) or plotted as settled (they would read as a collapse). */}
          {(data.rateTimeseries?.length ?? 0) > 1 && (
            <section className="space-y-2">
              <SectionHeader
                title="Reply rate over time"
                hint={`Share of each week's applications that got any answer, including rejections. A week needs about ${data.replyMaturityDays ?? 14} days before its number settles — until then it is drawn dashed, because the replies have not had time to arrive. Weeks with only one or two applications carry no line at all: a rate off two data points is not a trend. Shown all-time, not narrowed by the date range.`}
              />
              <Card padding="md">
                <LineChart
                  data={data.rateTimeseries}
                  height={170}
                  x={(d) => d.weekStart.slice(5)}
                  y={(d) => d.replyRatePct ?? 0}
                  nOf={(d) => d.applied}
                  matureOf={(d) => d.mature}
                  thinOf={(d) => d.thin}
                  ariaLabel="Reply rate by week applied"
                />
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-slate-500">
                  {data.rateTimeseries.filter((w) => w.mature && !w.thin).length > 1 && (
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block h-0.5 w-4" style={{ background: 'var(--c-accent)' }} /> settled
                    </span>
                  )}
                  {data.rateTimeseries.some((w) => !w.mature && !w.thin) && (
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block h-0.5 w-4" style={{ backgroundImage: 'repeating-linear-gradient(to right, var(--c-accent) 0 4px, transparent 4px 7px)' }} /> still coming in
                    </span>
                  )}
                  {data.rateTimeseries.some((w) => w.thin) && (
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block h-2 w-2 rounded-full border" style={{ borderColor: 'var(--c-text-3)' }} /> too few to read
                    </span>
                  )}
                </div>
              </Card>
            </section>
          )}

          {/* ── T259/T241 — jobs lost to the clock ─────────────────────────── */}
          {data.expiredUntriaged?.expired > 0 && (
            <section className="space-y-2">
              <SectionHeader
                title="Closed before you looked"
                hint="Recommendations you never triaged whose posting deadline has already passed. Only some boards publish a deadline, so this is a floor, not a total."
              />
              <Card padding="md">
                <p className="text-sm text-slate-200">
                  <span className="text-2xl font-semibold text-amber-300">{data.expiredUntriaged.expired}</span>{' '}
                  of the {data.expiredUntriaged.withDeadline} untriaged recommendations that carry a deadline have already closed
                  <span className="text-slate-500">
                    {' '}— your queue holds {data.expiredUntriaged.inQueue ?? data.expiredUntriaged.untriaged}, and these
                    {' '}{data.expiredUntriaged.untriaged - (data.expiredUntriaged.inQueue ?? data.expiredUntriaged.untriaged)}
                    {' '}have already left it.
                  </span>
                </p>
                {data.expiredUntriaged.bySource?.length > 0 && (
                  <p className="mt-1.5 text-xs text-slate-500">
                    {data.expiredUntriaged.bySource.map((r) => `${r.source} ${r.count}`).join(' · ')}
                  </p>
                )}
              </Card>
            </section>
          )}

          {/* Outcomes */}
          <section className="space-y-2">
            <SectionHeader title="Outcomes" hint="Current state of applications. “No response 14d+” is derived from inactivity, not an explicit status." />
            {/* The remainder: applications that are neither closed out nor gone
                quiet. Derived rather than served, so the donut adds up to the
                applied total instead of quietly dropping a slice. */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Tile label="Offers" value={data.outcomes.offer} accent="text-emerald-300" />
              <Tile label="Rejected" value={data.outcomes.rejected} accent="text-red-400" />
              <Tile label="Withdrawn" value={data.outcomes.withdrawn} accent="text-slate-300" />
              <Tile label="No response 14d+" value={data.outcomes.stalled} accent="text-amber-300" />
            </div>
            {/* Where every application currently stands. A donut is the right
                shape here and almost nowhere else on this page: it is one whole
                divided, not two things compared. */}
            <Card padding="md">
              <DonutChart
                data={[
                  { label: 'Still open', value: openNow, color: SERIES.accent },
                  { label: 'No response 14d+', value: data.outcomes.stalled, color: SERIES.warn },
                  { label: 'Rejected', value: data.outcomes.rejected, color: SERIES.down },
                  { label: 'Offers', value: data.outcomes.offer, color: SERIES.up },
                  // NOT TRACK: that is the colour of the empty ring drawn
                  // underneath every slice, so a withdrawn application rendered
                  // as a gap in the donut. Same mistake as the thin marker —
                  // a fill token used as a distinguishing colour.
                  { label: 'Withdrawn', value: data.outcomes.withdrawn, color: LABEL },
                  // Only when there are any: a permanent zero slice in a donut
                  // is a legend entry that teaches the reader nothing.
                  ...(data.outcomes.offTracker
                    ? [{ label: 'Off the tracker', value: data.outcomes.offTracker, color: LABEL_STRONG }]
                    : []),
                ]}
                colorOf={(d) => d.color}
                centerLabel="applications"
                ariaLabel="Where your applications currently stand"
              />
            </Card>
            {/* Phase 4 — disposition breakdown: HOW offers/rejections resolved. */}
            {data.dispositions && (data.dispositions.offer.total > 0 || data.dispositions.rejection.total > 0) && (
              <div className="grid gap-3 lg:grid-cols-2">
                {data.dispositions.offer.total > 0 && (
                  <Card padding="md">
                    <p className="mb-2 text-xs font-semibold text-slate-300">How offers resolved <span className="text-slate-500">({data.dispositions.offer.total})</span></p>
                    <DispoRows
                      rows={[
                        ['Accepted', data.dispositions.offer.accepted, 'text-emerald-300'],
                        ['I declined', data.dispositions.offer.declined, 'text-slate-300'],
                        ['Expired / rescinded', data.dispositions.offer.expired, 'text-red-400'],
                        ['Lost after offer', data.dispositions.offer.lost, 'text-red-400'],
                        ['Still deciding', data.dispositions.offer.pending, 'text-amber-300'],
                      ]}
                    />
                  </Card>
                )}
                {data.dispositions.rejection.total > 0 && (
                  <Card padding="md">
                    <p className="mb-2 text-xs font-semibold text-slate-300">Where rejections happened <span className="text-slate-500">({data.dispositions.rejection.total})</span></p>
                    <DispoRows
                      rows={[
                        ['After applying (no screen)', data.dispositions.rejection.afterApply, 'text-slate-300'],
                        ['After screening', data.dispositions.rejection.afterScreen, 'text-brand-400'],
                        ['After interview', data.dispositions.rejection.afterInterview, 'text-amber-300'],
                        ['After offer', data.dispositions.rejection.afterOffer, 'text-emerald-300'],
                        ['No reason given', data.dispositions.rejection.noReason, 'text-slate-400'],
                        ['Not recorded', data.dispositions.rejection.unspecified, 'text-slate-500'],
                      ]}
                    />
                    {data.dispositions.rejection.unspecified > 0 && (
                      <p className="mt-2 text-[10px] text-slate-600">Tag the stage in a closed application&apos;s drawer to fill these in.</p>
                    )}
                  </Card>
                )}
              </div>
            )}
          </section>

        </>
      )}

      {/* Triage quality — does a higher score actually get you to apply? Shown
          even at 0 applied, since it's about the dismiss/apply decision. */}
      {!loading && !error && data?.scoreBands?.some((b) => b.actedOn > 0) && (
        <CollapsibleSection id="score-trust" title="Is the score worth trusting?" hint={`Of jobs you acted on, apply-rate per score band = applied ÷ (applied + dismissed). If higher bands convert higher, the score is a good triage filter.${allTimeNote}`}
            summary={`${data.scoreBands.filter((b) => b.actedOn >= SCORE_MIN_N).length} bands with enough data`}>
          <Card padding="md">
            <BarChart
              data={data.scoreBands}
              height={104}
              showValues
              x={(b) => b.label}
              y={(b) => b.applyRatePct ?? 0}
              xLabel={(b) => b.label}
              yAxis
              domainMax={100}
              yTickText={(t) => `${t}%`}
              colorOf={() => SERIES.up}
              mutedOf={(b) => b.actedOn < SCORE_MIN_N}
              valueText={(b) => (b.actedOn < SCORE_MIN_N ? '—' : `${b.applyRatePct ?? 0}%`)}
              tooltip={(b) => `${b.label}: ${b.applied}/${b.actedOn} applied${b.applyRatePct == null ? '' : ` (${b.applyRatePct}%)`}`}
              ariaLabel="Apply-rate by score band"
            />
            <p className="mb-2 mt-1 text-[10px] text-slate-500">Apply-rate per band · greyed bars have under {SCORE_MIN_N} acted-on (too few to read)</p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[26rem] text-xs">
              <thead>
                <tr className="text-left uppercase tracking-wide text-slate-500">
                  <th scope="col" className="pb-1.5 pr-3 font-medium">Score band</th>
                  <th scope="col" className="pb-1.5 pr-3 text-right font-medium">Applied</th>
                  <th scope="col" className="pb-1.5 pr-3 text-right font-medium">Dismissed</th>
                  <th scope="col" className="pb-1.5 text-right font-medium">Apply-rate</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {data.scoreBands.map((b) => (
                  <tr key={b.label} className={'border-t border-slate-800/60 ' + (b.actedOn < SCORE_MIN_N ? 'opacity-50' : '')}>
                    <td className="py-1.5 pr-3 font-mono">
                      {/* T331 — the one dimension you could read and not open.
                          Source, company and country rows have drilled into the
                          board since June; a band had nowhere to land until the
                          board learned the key. Only rows with something to show
                          are clickable — a link to an empty list is a worse
                          answer than no link. */}
                      {b.applied > 0 ? (
                        <button
                          type="button"
                          onClick={() => drillTo('band', b.label)}
                          aria-label={`Score band ${b.label} — view applications`}
                          className="underline decoration-dotted underline-offset-2 hover:text-brand-400"
                        >
                          {b.label}
                        </button>
                      ) : (
                        b.label
                      )}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-emerald-300">{b.applied}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-slate-400">{b.dismissed}</td>
                    <td className="py-1.5 text-right tabular-nums text-slate-100">{rateCell(b.applyRatePct, b.actedOn, SCORE_MIN_N)}</td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            {/* Phase 4 item 5 — does a higher score reach FURTHER than apply-vs-
                dismiss? Deterministic correlation, gated + honest about the rungs
                it can't calibrate yet (you can't calibrate "offer" with 0 offers). */}
            {data.scoreCalibration?.sufficient ? (
              <div className="mt-3 border-t border-slate-800/60 pt-2">
                <p className="text-xs text-slate-400">
                  Score → how far it gets with recruiters: <span className="text-slate-200">{data.scoreCalibration.verdict}</span>
                  {data.scoreCalibration.correlation != null && (
                    <span className="text-slate-500"> (r={data.scoreCalibration.correlation}, n={data.scoreCalibration.n} applied)</span>
                  )}
                  {data.scoreCalibration.maxOrdinalSeen < 3 && (
                    <span className="text-slate-600">
                      {' '}· only calibrated up to {['applied', 'screening', 'interview', 'offer'][data.scoreCalibration.maxOrdinalSeen]} so far — higher rungs need more data
                    </span>
                  )}
                </p>
                {data.scoreCalibration.byBand?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-500">
                    {data.scoreCalibration.byBand.map((b) => (
                      <span key={b.label} className={b.n < 3 ? 'opacity-50' : ''}>
                        <span className="font-mono text-slate-400">{b.label}</span> avg {b.meanOrdinal} <span className="text-slate-600">(n={b.n})</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              data.scoreCalibration && (
                <p className="mt-3 border-t border-slate-800/60 pt-2 text-xs text-slate-600">
                  Score-vs-recruiter-outcome calibration needs {data.scoreCalibration.need}+ applied jobs ({data.scoreCalibration.n} so far).
                </p>
              )
            )}
          </Card>
        </CollapsibleSection>
      )}

      {!loading && !error && data && data.totals.applied > 0 && (
        <>

          {/* What's converting — source / country / company / CV version */}
          <CollapsibleSection id="whats-converting" title="What's converting" hint="Advanced = reached screening or beyond ÷ sent (a rejection is a reply, not an advance). Rows under 3 samples show a muted dash — too few to read. Watch the raw counts."
              summary={`${(data.bySource ?? []).length} sources · ${(data.byCompany ?? []).length} companies`}>
            <div className="mb-2 flex justify-end">
              <SegmentedControl
                label="Order"
                value={rateSort}
                options={RATE_SORT_OPTIONS}
                onChange={(v) => setFilters({ rateSort: v })}
              />
              {/* T336 — the five cuts as one long-format file. The question
                  that outruns this screen is always a second cut — "jobindex
                  in Denmark with v3" — which the page cannot show and a
                  spreadsheet can. */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => downloadCsv(
                  csvFilename('converting'),
                  toCsv(CONVERTING_COLUMNS, convertingRows(data, MIN_N)),
                )}
              >
                ↓ CSV
              </Button>
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              <Card padding="md">
                <p className="mb-2 text-xs font-semibold text-slate-300">By source <span className="font-normal text-slate-600">· click to drill in</span></p>
                <RateTable sort={rateSort} rows={data.bySource || []} keyOf={(r) => r.source} labelOf={(r) => r.source} onSelect={(r) => drillTo('source', r.source)} onSelectGhosted={(r) => drillTo('source', r.source, { show: 'stale' })} />
              </Card>
              <Card padding="md">
                <p className="mb-2 text-xs font-semibold text-slate-300">By country <span className="font-normal text-slate-600">· click to drill in</span></p>
                <RateTable sort={rateSort} rows={data.byCountry || []} keyOf={(r) => r.country} labelOf={(r) => r.country} onSelect={(r) => drillTo('country', r.country)} onSelectGhosted={(r) => drillTo('country', r.country, { show: 'stale' })} />
              </Card>
              <Card padding="md">
                <p className="mb-2 text-xs font-semibold text-slate-300">By company <span className="font-normal text-slate-600">· click to drill in</span></p>
                <RateTable sort={rateSort} rows={data.byCompany || []} keyOf={(r) => r.company} labelOf={(r) => r.company} onSelect={(r) => drillTo('company', r.company)} onSelectGhosted={(r) => drillTo('company', r.company, { show: 'stale' })} />
              </Card>
              <Card padding="md">
                <p className="mb-2 text-xs font-semibold text-slate-300">By CV version</p>
                {/* T334 — the fifth drill target, mirroring T331's band.
                    "v3 advances at 40% and v2 at 12%" is the most actionable
                    line on this card and it was the one row you could not
                    open. */}
                <RateTable
                  sort={rateSort}
                  rows={(data.byCvVersion || []).map((c) => ({ ...c, ghosted: 0 }))}
                  keyOf={(r) => r.version}
                  labelOf={(r) => `v${r.version}`}
                  onSelect={(r) => drillTo('cv', String(r.version))}
                />
              </Card>
              {/* T260(3) — by work arrangement. Its sibling by seniority is
                  absent on purpose: no ingested job carries that field. */}
              {(data.byRemote || []).length > 1 && (
                <Card padding="md">
                  <p className="mb-2 text-xs font-semibold text-slate-300">By work arrangement</p>
                  {/* T290 — `sort={rateSort}` like every sibling on this row.
                      It was the only table in the section that ignored the Order
                      control above it, so changing the order visibly re-sorted
                      four tables and left the fifth alone — the section holds
                      five (source, country, company, CV version, arrangement) —
                      which reads as that table having no order rather than as a
                      missing prop. */}
                  <RateTable sort={rateSort} rows={data.byRemote} keyOf={(r) => r.remote} labelOf={(r) => r.remote} />
                </Card>
              )}
            </div>
          </CollapsibleSection>

          {/* ── T260 — when you apply, and how fast ────────────────────────
              Two questions the operator can act on this afternoon, both
              answerable from data that has been in the database all along and
              that nothing ever looked at: appliedAt's day of week, and the gap
              between a posting going up and him answering it. Reply rate, not
              advance rate — the question is whether a human answers at all. */}
          {(data.weekday?.some((d) => d.applied > 0) || data.applyLag?.arms?.some((a) => a.applied > 0)) && (
            <CollapsibleSection id="when-you-apply" title="When you apply"
                // Both halves come from `recs`, which IS narrowed by the range
                // picker — unlike the lifecycle cards, so allTimeNote would be
                // the opposite of the truth here.
                hint={`Reply rate by the weekday you sent, and by how fresh the posting was. A bar is greyed under ${RATE_MIN_N} applications — too few to read.${range !== 'all' ? ' Narrowed by the date range above.' : ''}`}>
              <div className="grid gap-3 lg:grid-cols-2">
                <Card padding="md">
                  <p className="mb-2 text-xs font-semibold text-slate-300">By weekday sent</p>
                  <BarChart
                    data={data.weekday || []}
                    height={116}
                    showValues
                    yAxis
                    domainMax={100}
                    yTickText={(t) => `${t}%`}
                    x={(d) => d.label}
                    y={(d) => d.replyRatePct ?? 0}
                    xLabel={(d) => d.label.slice(0, 3)}
                    colorOf={() => SERIES.accent}
                    mutedOf={(d) => d.applied < RATE_MIN_N}
                    valueText={(d) => (d.applied < RATE_MIN_N ? '—' : `${d.replyRatePct ?? 0}%`)}
                    tooltip={(d) => `${d.label}: ${d.replied}/${d.applied} heard back${d.applied < RATE_MIN_N ? ' · too few to read' : ` (${d.replyRatePct}%)`}`}
                    ariaLabel="Reply rate by the weekday you applied"
                  />
                </Card>
                {data.applyLag && (
                  <Card padding="md">
                    <p className="mb-2 text-xs font-semibold text-slate-300">By how fresh the posting was</p>
                    <RateTable
                      rows={(data.applyLag.arms || []).map((a) => ({
                        ...a,
                        // RateTable speaks advance-rate; these arms speak reply-rate.
                        advanced: a.replied,
                        advanceRatePct: a.replyRatePct,
                        ghosted: 0,
                      }))}
                      keyOf={(r) => r.label}
                      labelOf={(r) => r.label}
                      minN={RATE_MIN_N}
                      verb="heard back"
                    />
                    <p className="mt-2 text-[10px] text-slate-500">
                      Median gap between posting and applying: {data.applyLag.medianLagDays ?? '—'}d
                      {applyLagFootnote(data.applyLag)}
                    </p>
                  </Card>
                )}
              </div>
            </CollapsibleSection>
          )}



        </>
      )}

      {/* Trends & consistency — weekly pace, momentum, streak, activity heatmap */}
      {!loading && !error && data?.trend?.byWeek?.length > 0 && data.trend.momentum && (
        <CollapsibleSection id="trends" title="Trends & consistency" hint={`Are you keeping pace? Weekly applications, your streak, and daily activity.${allTimeNote}`}
            summary={`${data.trend.byWeek.length} weeks · ${data.streak?.current ?? 0}-day streak`}>
          <Card padding="md">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              {/* T334 — `min-w-0`, because `flex-1` defaults to
                  `min-width: auto` and the chart inside is a replaced element
                  with a 600px intrinsic width. Without it this column cannot
                  shrink, and the streak numbers beside it get crushed. */}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
                  <span>
                    <span className="text-2xl font-semibold text-slate-100">{data.trend.momentum.thisWeek}</span>{' '}
                    <span className="text-slate-500">this week</span>
                  </span>
                  {(() => {
                    const m = data.trend.momentum;
                    // T290 — the arrow is decided against the 4-week average
                    // when there is one, and it used to be printed immediately
                    // before "vs N last wk". So a week of 5 against last week's
                    // 4 and a 4-week average of 7 showed ▼ beside "vs 4 last
                    // wk" — down against a number it was up on. The label now
                    // names whichever reference the arrow actually used.
                    const usingAvg = m.prev4Avg != null;
                    const ref = usingAvg ? m.prev4Avg : m.lastWeek;
                    const up = m.thisWeek >= (ref || 0);
                    return (
                      <span className={up ? 'text-emerald-300' : 'text-amber-300'}>
                        {up ? '▲' : '▼'} vs {usingAvg ? `${m.prev4Avg} 4-wk avg` : `${m.lastWeek} last wk`}
                        {usingAvg ? ` · ${m.lastWeek} last wk` : <> · <NoData /> 4-wk avg</>}
                      </span>
                    );
                  })()}
                </div>
                <div className="mt-2">
                  {/* T334 — 110px and an axis, from 48 and none.
                      This is one of the four highest stare-time cards on the
                      page and it was a bare sparkline: you could see that a bar
                      was taller than its neighbour and not what either one
                      counted, nor which week you were looking at. `padBottom`
                      is 16 and `padTop` 14, so 48px never had room for the
                      label gutter the axis needs. */}
                  <BarChart
                    data={data.trend.byWeek}
                    height={110}
                    yAxis
                    // T342 — the 4-week average as a dashed reference. The
                    // arrow above already decides "up or down" against this
                    // number, and until now the chart underneath gave no way to
                    // see it: you could read that a bar was taller than its
                    // neighbour and not whether it was above trend.
                    refValue={data.trend.momentum.prev4Avg}
                    // Names the line without restating the figure: the
                    // sentence directly above already prints it, and saying the
                    // same number twice six pixels apart is noise.
                    refLabel="4-wk avg"
                    xLabel={(w) => String(w.weekStart).slice(5)}
                    x={(w) => `Week of ${w.weekStart}`}
                    y={(w) => w.count}
                    colorOf={(w, i) => (i === data.trend.byWeek.length - 1 ? SERIES.accent : softFill(SERIES.accent))}
                    strokeOf={(w, i) => (i === data.trend.byWeek.length - 1 ? undefined : SERIES.accent)}
                    tooltip={(w) => `Week of ${w.weekStart}: ${w.count} application${w.count === 1 ? '' : 's'}`}
                    ariaLabel={`Weekly applications, last ${data.trend.byWeek.length} weeks`}
                  />
                </div>
                <p className="mt-1 text-[10px] text-slate-500">Last {data.trend.byWeek.length} weeks (applications / week) · hover a bar for the count</p>
              </div>
              {data.streak && (
                <div className="lg:text-right">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Streak</p>
                  <p className="mt-0.5 text-2xl font-semibold text-amber-300">
                    {data.streak.current}
                    <span className="ml-1 text-sm text-slate-500">day{data.streak.current === 1 ? '' : 's'}</span>
                  </p>
                  <p className="text-[10px] text-slate-500">longest {data.streak.longest} · {data.streak.threshold}/day to count</p>
                </div>
              )}
            </div>
            {data.heatmap?.length > 0 && (
              <div className="mt-4">
                <p className="mb-1.5 text-[10px] uppercase tracking-wide text-slate-500">Last 12 weeks · hover a day for the count</p>
                <HeatmapChart data={data.heatmap} ariaLabel="Daily application activity, last 12 weeks" />
              </div>
            )}
            {data.weeklyPace && (
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-800/60 pt-3 text-xs">
                {data.weeklyPace.weeklyGoal ? (
                  <span className="text-slate-400">
                    Weekly goal <span className="font-semibold text-slate-200">{data.weeklyPace.weeklyGoal}</span>
                    <span className="text-slate-500"> · this week {data.weeklyPace.thisWeek} ({pctText(data.weeklyPace.onPacePct)})</span>
                  </span>
                ) : (
                  <span className="text-slate-500">Set a daily goal above to track a weekly target.</span>
                )}
                <span className="text-slate-400">
                  Sustainable pace <span className="font-semibold text-slate-200">{data.weeklyPace.sustainablePerWeek}</span>/wk
                  <span className="text-slate-500">
                    {data.weeklyPace.projectedFourWeeks != null
                      ? ` · ~${data.weeklyPace.projectedFourWeeks} projected over 4 wks`
                      : ' · projection builds with a few more weeks'}
                  </span>
                </span>
              </div>
            )}
          </Card>
        </CollapsibleSection>
      )}



      {/* Weekly cohorts — how each week's batch has progressed since applying */}
      {!loading && !error && data?.cohorts?.some((c) => c.applied > 0) && (
        <CollapsibleSection id="cohorts" title="Weekly cohorts" hint={`Each row is the batch you applied to that week, and how far it's progressed since. Older weeks have had more time to convert — compare like with like.${allTimeNote}`}
            summary={`${data.cohorts.filter((c) => c.applied > 0).length} weekly batches`}>
          <Card padding="md">
            {/* T329 — a progression grid, not a table of counts. The table left
                the reader to notice that the bottom row was four applications
                sent yesterday; the grid marks an immature batch as still
                maturing rather than drawing it as the worst week on the page. */}
            <CohortGrid cohorts={data.cohorts} maturityDays={data.replyMaturityDays ?? 14} />
          </Card>
        </CollapsibleSection>
      )}

      {!loading && !error && data && data.totals.applied > 0 && (
        <>

          {/* Time in stage — full distribution + an overdue nudge */}
          {data.timeInStageDist &&
            (data.timeInStageDist.transitions.some((t) => t.n > 0) || data.timeInStageDist.overdue.length > 0) && (
              <CollapsibleSection id="time-in-stage" title="Time in stage" hint={`How long each step takes, across all your applications. p90 means 9 in 10 cleared the step faster than that. Median & p90 need at least 3 timed transitions to mean anything — under that the row is greyed and they show a dash, leaving only the raw range.${allTimeNote}`}>
                <Card padding="md">
                  {/* T330 — a range bar per transition instead of five numbers in a row.
                      The table left the reader holding min/median/p90/max in
                      their head to picture one distribution. The honesty rule
                      is the table's own, kept: below MIN_N no median and no
                      whisker, because a percentile over two observations is a
                      number dressed as a statistic. */}
                  <BoxRange rows={data.timeInStageDist.transitions} minN={MIN_N} />
                  {/* T261 — this sentence names the operator's actual problem
                      and used to be inert text, three lines above a drill
                      handler that already worked. The stale filter it wants
                      exists on the tracker. */}
                  {data.timeInStageDist.overdue.length > 0 && (
                    <p className="mt-3 text-xs text-amber-300">
                      {data.timeInStageDist.overdue
                        .map((o) => `${o.count} ${o.stage} ${o.count === 1 ? 'application' : 'applications'} past ~${daysText(o.thresholdDays)}`)
                        .join(' · ')}
                      <span className="text-slate-500"> — worth a nudge. </span>
                      <button
                        type="button"
                        onClick={() => navigate('/applications?show=stale')}
                        className="underline decoration-dotted hover:text-brand-400"
                      >
                        Open the ones that have gone quiet
                      </button>
                    </p>
                  )}
                </Card>
              </CollapsibleSection>
            )}

          {/* Recruiter response time */}
          {rt && (
            <CollapsibleSection id="response-time" title="Recruiter response time" hint="Days from applying to the first reply (a rejection counts as a reply), for applications that got one."
                summary={rt?.n > 0 && rt.medianDays != null ? `median ${rt.medianDays}d · ${rt.n} replies` : 'no replies yet in this range'}>
              <Card padding="md">
                {rt.n > 0 ? (
                  <>
                    <p className="text-sm text-slate-300">
                      Median <span className="font-semibold text-slate-100">{daysText(rt.medianDays)}</span> to first response
                      <span className="text-slate-500"> · {rt.minDays}–{rt.maxDays}d range · {rt.n} response{rt.n === 1 ? '' : 's'}</span>
                      {rt.stillWaiting > 0 && <span className="text-slate-500"> · {rt.stillWaiting} still waiting</span>}
                    </p>
                    <div className="mt-3">
                      <BarChart
                        data={Object.entries(rt.histogram).map(([bucket, n]) => ({ bucket, n }))}
                        height={84}
                        showValues
                        x={(d) => (d.bucket === '30+' ? '30d+' : `${d.bucket}d`)}
                        y={(d) => d.n}
                        xLabel={(d) => (d.bucket === '30+' ? '30d+' : `${d.bucket}d`)}
                        yAxis
                        tooltip={(d) => `${d.bucket === '30+' ? '30d+' : `${d.bucket} days`}: ${d.n} response${d.n === 1 ? '' : 's'}`}
                        ariaLabel="Time-to-first-response distribution"
                      />
                    </div>
                    {rt.bySource.length > 1 && (
                      <p className="mt-3 text-xs text-slate-500">
                        {/* T334 — the small-n floor every other number on this
                            page carries, applied here too. This line printed
                            "jobindex 3d (1)" beside "arbeitnow 18d (12)" in the
                            same weight, and a median over one reply is not a
                            median. Same treatment as `rateCell`: the figure is
                            withheld, the count is not. */}
                        By source:{' '}
                        {rt.bySource.map((src, i) => (
                          <span key={src.source}>
                            {i > 0 && ' · '}
                            <span className={src.n < MIN_N ? 'text-slate-600' : undefined}
                              title={src.n < MIN_N ? 'too few replies to read a median' : undefined}
                            >
                              {src.source}{' '}
                              {src.n < MIN_N ? <NoData /> : daysText(src.medianDays)}
                              {' '}({src.n})
                            </span>
                          </span>
                        ))}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-slate-500">
                    No responses yet{rt.stillWaiting > 0 ? ` — ${rt.stillWaiting} application${rt.stillWaiting === 1 ? '' : 's'} still waiting.` : '.'}
                  </p>
                )}
              </Card>
            </CollapsibleSection>
          )}

          {/* Follow-up effectiveness + AI cost/ROI */}
          {(data.followUp?.sent > 0 || data.aiRoi) && (
            <CollapsibleSection id="effort-spend" title="Effort & spend" hint="Did the check-ins and AI assets pay off? Small samples — watch the raw counts, not the %.">
              <div className="grid gap-3 lg:grid-cols-2">
                {data.followUp?.sent > 0 && (
                  <Card padding="md">
                    <p className="mb-1 text-xs font-semibold text-slate-300">Follow-up effectiveness</p>
                    <p className="text-sm text-slate-300">
                      <span className="font-semibold text-slate-100">{data.followUp.movedAfter}</span> of {data.followUp.sent} follow-ups got a reply afterward{' '}
                      <span className="text-slate-500">({rateCell(data.followUp.ratePct, data.followUp.sent)})</span>
                    </p>
                    <p className="mt-1 text-[10px] text-slate-600">A reply after sending isn&apos;t proof it caused it, but it&apos;s a useful signal.</p>
                  </Card>
                )}
                {data.aiRoi && (
                  <Card padding="md">
                    <p className="mb-1 text-xs font-semibold text-slate-300">AI assets &amp; spend</p>
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[26rem] text-xs">
                      <thead>
                        <tr className="text-left uppercase tracking-wide text-slate-500">
                          <th scope="col" className="pb-1 pr-3 font-medium">Asset at apply-time</th>
                          <th scope="col" className="pb-1 pr-3 text-right font-medium">With</th>
                          <th scope="col" className="pb-1 text-right font-medium">Without</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-300">
                        {[['Cover letter', 'coverLetter'], ['Tailored CV', 'tailoredCv']].map(([label, key]) => (
                          <tr key={key} className="border-t border-slate-800/60">
                            <td className="py-1.5 pr-3">{label}</td>
                            <td className="py-1.5 pr-3 text-right tabular-nums">
                              {rateCell(data.aiRoi[key].with.advanceRatePct, data.aiRoi[key].with.applied)}{' '}
                              <span className="text-slate-600">({data.aiRoi[key].with.advanced}/{data.aiRoi[key].with.applied})</span>
                            </td>
                            <td className="py-1.5 text-right tabular-nums">
                              {rateCell(data.aiRoi[key].without.advanceRatePct, data.aiRoi[key].without.applied)}{' '}
                              <span className="text-slate-600">({data.aiRoi[key].without.advanced}/{data.aiRoi[key].without.applied})</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      </table>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      Total AI spend <span className="text-slate-300">${data.aiRoi.spend.totalUsd}</span>
                      {data.aiRoi.spend.costPerInterview != null && <> · ${data.aiRoi.spend.costPerInterview}/interview</>}
                      {data.aiRoi.spend.costPerOffer != null && <> · ${data.aiRoi.spend.costPerOffer}/offer</>}
                    </p>
                  </Card>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* A/B variant (existing endpoint) */}
          {variants && variants.total > 0 && (
            <CollapsibleSection id="cv-ab" title="Tailored-CV A/B" hint="By the Professional-Profile variant you picked. “Advanced” here = currently past 'applied' (a rec later rejected counts as terminal), so it reads slightly stricter than the funnel's response rate above.">
              <Card padding="md">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[26rem] text-xs">
                  <thead>
                    <tr className="text-left uppercase tracking-wide text-slate-500">
                      <th scope="col" className="pb-1.5 pr-4 font-medium">Variant</th>
                      <th scope="col" className="pb-1.5 pr-4 text-right font-medium">Picked</th>
                      <th scope="col" className="pb-1.5 pr-4 text-right font-medium">Advanced</th>
                      <th scope="col" className="pb-1.5 text-right font-medium">Rate</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    {['A', 'B'].map((v) => (
                      <tr key={v} className="border-t border-slate-800/60">
                        <td className="py-1.5 pr-4">Variant {v}</td>
                        <td className="py-1.5 pr-4 text-right tabular-nums">{variants[v].picked}</td>
                        <td className="py-1.5 pr-4 text-right tabular-nums">{variants[v].advanced}</td>
                        {/* T343 — the same floor the note below uses. */}
                        <td className="py-1.5 text-right tabular-nums text-slate-100">{rateCell(variants[v].advanceRatePct, variants[v].picked, AB_MIN_N)}</td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  {significanceNote(variants.A.picked, variants.A.advanced, variants.B.picked, variants.B.advanced)}
                </p>
              </Card>
            </CollapsibleSection>
          )}


        </>
      )}

      {/* Why you dismiss + what separates apply-vs-dismiss (triage feedback) */}
      {!loading && !error && (data?.dismissReasons?.total > 0 || data?.scoreBreakdownDecomp?.length > 0) && (
        <CollapsibleSection id="triage-decoded" title="Your triage, decoded" hint={`Why you pass on jobs, and which score signal actually moves your apply decision.${allTimeNote}`}
            summary={dismissSummary(data.dismissReasons)}>
          <div className="grid gap-3 lg:grid-cols-2">
            {data.dismissReasons?.total > 0 && (
              <Card padding="md">
                <p className="mb-2 text-xs font-semibold text-slate-300">Why you dismiss jobs</p>
                {/* T258(2) — was an inline second copy of DispoRows, differing
                    only in label width. */}
                {/* Why you pass: one whole, split. The bars stay underneath —
                    a donut is good at "what dominates" and poor at ranking the
                    tail, and the tail is where a parser problem hides. */}
                <DonutChart
                  data={[
                    ...(data.dismissReasons.presets || []).map((p) => ({ label: p.reason, value: p.count })),
                    { label: 'Other', value: data.dismissReasons.other },
                  ]}
                  colorOf={(_d, i) => DISMISS_COLORS[i % DISMISS_COLORS.length]}
                  centerLabel="dismissals"
                  ariaLabel="Why you dismiss jobs"
                />
                <div className="mt-3">
                  <DispoRows
                    labelWidth="w-36"
                    empty="No tagged dismissals yet."
                    rows={[
                      ...(data.dismissReasons.presets || []).map((p) => [p.reason, p.count]),
                      ['Other', data.dismissReasons.other],
                    ]}
                  />
                </div>
                {data.dismissReasons.bulkCleanup > 0 && (
                  <p className="mt-2 text-[10px] text-slate-600">{data.dismissReasons.bulkCleanup} bulk preference-cleanup tags excluded.</p>
                )}
              </Card>
            )}
            {data.scoreBreakdownDecomp?.length > 0 && (
              <Card padding="md">
                <p className="mb-2 text-xs font-semibold text-slate-300">What makes you apply</p>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[26rem] text-xs">
                  <thead>
                    <tr className="text-left uppercase tracking-wide text-slate-500">
                      <th scope="col" className="pb-1.5 pr-3 font-medium">Signal</th>
                      <th scope="col" className="pb-1.5 pr-3 text-right font-medium text-emerald-300">Applied avg</th>
                      <th scope="col" className="pb-1.5 text-right font-medium text-slate-400">Dismissed avg</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    {/* T335 — the counts, and the floor.
                        The service has returned `appliedN`/`dismissedN` all
                        along and the table showed neither, so a mean over four
                        rows sat beside one over eighty in the same weight.
                        Every other number on this page carries a small-n floor;
                        this one now does too. */}
                    {data.scoreBreakdownDecomp.map((c) => (
                      <tr key={c.component} className="border-t border-slate-800/60">
                        <td className="py-1.5 pr-3 capitalize">{c.component}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums text-emerald-300">
                          {c.appliedMean == null || c.appliedN < MIN_N
                            ? <NoData />
                            : <>{c.appliedMean}<span className="ml-1 text-[10px] text-slate-600">n={c.appliedN}</span></>}
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-slate-400">
                          {c.dismissedMean == null || c.dismissedN < MIN_N
                            ? <NoData />
                            : <>{c.dismissedMean}<span className="ml-1 text-[10px] text-slate-600">n={c.dismissedN}</span></>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </div>
                {/* T335 — scoped. "A bigger gap drives your decision more" is
                    true of rules and semantic, which are earned points. It is
                    backwards for `negative`, which is a penalty SUBTRACTED for
                    resembling something he dismissed: a bigger gap there would
                    mean he applies to more of what he rejects. */}
                <p className="mt-2 text-[10px] text-slate-600">
                  For <span className="font-mono">rules</span> and <span className="font-mono">semantic</span>, a bigger gap
                  means that signal drives your apply decision more.{' '}
                  <span className="font-mono">negative</span> is a penalty subtracted for resembling something you dismissed,
                  so there a gap near zero is the healthy reading.
                </p>
              </Card>
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* AI retrospective — on-demand narrative over the real numbers */}
      {!loading && !error && data?.totals?.applied > 0 && <AnalyticsRetroCard />}

      {/* Rejection/ghosting clustering — on-demand, gated on enough closed apps */}
      {!loading && !error && data?.totals?.applied > 0 && <AnalyticsClustersCard />}
    </div>
  );
}
